import { baseApi } from "./baseApi";
import type { GetCallLogsResponse } from "../../pages/Crm/components/LeadDetails/components/LeadCallLogs/utils/types";
import { TaskItem } from "../../pages/Crm/components/LeadDetails/components/LeadTask/utils/types";
import {
  GetAllTasksParams,
  GetAllTasksResponse,
  TaskAnalyticsResponse,
  BulkUpdateTasksResponse,
} from "../../pages/Crm/components/TasksFollowUps/utils/types";
import {
  getMockThreadListResponse,
  getMockThreadMailsResponse,
  getMockSummaryResponse,
  getMockAssignResponse,
} from "../../pages/Crm/components/Inbox/utils/constants";
import type {
  InboxThreadListResponse,
  InboxThreadMailsResponse,
  InboxSummaryResponse,
  InboxThreadListParams,
  InboxSummaryParams,
  InboxThread,
  InboxMail,
  InboxAssignAgentsRequest,
} from "../../pages/Crm/components/Inbox/utils/types";
import type { LeadCreateDefaults } from "../../pages/Communications/components/CommunicationDetailsPanel/utils/types";

export const CRM_ATTRIBUTION_FIELDS = [
  "attribution_channel",
  "standard_medium",
  "standard_source",
  "device_type",
] as const;
export type CrmAttributionField = (typeof CRM_ATTRIBUTION_FIELDS)[number];

export const crmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLeads: builder.mutation({
      query: (payload) => {
        return {
          url: "/dashboard/lead-listing",
          method: "POST",
          body: payload,
        };
      },
    }),

    crmLeadSearch: builder.mutation({
      query: (query: string) => ({
        url: `/dashboard/lead-search?search_query=${query}`,
        method: "GET",
      }),
    }),

    linkLead: builder.mutation({
      query: (body: { primary_lead_id: string; secondary_lead_id: string }) => ({
        url: `/lms/leads/link`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "LeadDetails", id: "LeadDetails" }],
    }),

    getLeadDetailsById: builder.query({
      query: (leadId: string) => ({
        url: `/dashboard/leads/${leadId}`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadDetails", id: "LeadDetails" }],
    }),

    getAllLeadStages: builder.query({
      query: (params?: { view_id?: string }) => ({
        url: `/dashboard/crm/lead-stages${params?.view_id ? `?view_id=${params.view_id}` : ""}`,
        method: "GET",
      }),
    }),

    getConversationChannelCountById: builder.query({
      query: (id: string) => ({
        url: `/dashboard/leads/${id}/conv-channel-count`,
        method: "GET",
      }),
    }),

    getTranscriptSummaryById: builder.query({
      query: (id: string) => ({
        url: `/dashboard/leads/${id}/lead-summary`,
        method: "GET",
      }),
    }),

    getBillingInformationById: builder.query({
      query: (id: string) => ({
        url: `/dashboard/leads/${id}/get-billing-info`,
        method: "GET",
      }),
    }),

    getOutboundCommunicationStatusById: builder.query({
      query: (id: string) => ({
        url: `/dashboard/leads/${id}/get-enabled-channels`,
        method: "GET",
      }),
    }),

    updateOutboundCommunicationStatusById: builder.mutation({
      query: ({
        id,
        channelType,
        status,
      }: {
        id: string;
        channelType: string;
        status: boolean;
      }) => ({
        url: `dashboard/leads/${id}/toggle-channel/${channelType}?active=${status}`,
        method: "PUT",
        body: channelType === "widget" ? { toggle_all_channels: true } : {},
      }),
    }),

    updateGlobalOutboundCommunicationStatusById: builder.mutation({
      query: ({ id, status }: { id: string; status: boolean }) => ({
        url: `dashboard/leads/${id}/toggle-all-channels?active=${status}`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "LeadLogs" }],
    }),

    getLeadLostStageReasons: builder.query({
      query: (params?: { stage_key?: string }) => ({
        url: `/lms/leads/lost_reasons${params?.stage_key ? `?stage_key=${params.stage_key}` : ""}`,
        method: "GET",
      }),
    }),

    getLeadCancellationReasons: builder.query({
      query: (params?: { stage_key?: string }) => ({
        url: `/lms/leads/cancellation_reasons${params?.stage_key ? `?stage_key=${params.stage_key}` : ""}`,
        method: "GET",
      }),
    }),

    getLeadAttachmentsById: builder.query({
      query: (leadId: string) => ({
        url: `/dashboard/leads/${leadId}/attachments`,
        method: "GET",
      }),
    }),

    getAllUsersByStatus: builder.query({
      query: () => ({
        url: "/users/get-users-by-status/active",
        method: "GET",
      }),
    }),

    getNotesByLeadId: builder.query({
      query: (leadId: string) => ({
        url: `/dashboard/leads/${leadId}/notes`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadDetails", id: "Notes" }],
    }),

    addLeadNote: builder.mutation({
      query: ({
        leadId,
        summary,
        tagged_user_ids,
      }: {
        leadId: string;
        summary: string;
        tagged_user_ids: string[];
      }) => ({
        url: `/dashboard/leads/${leadId}/notes`,
        method: "POST",
        body: { summary, tagged_user_ids },
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "Notes" }],
    }),

    editLeadNote: builder.mutation({
      query: ({
        leadId,
        noteId,
        summary,
        tagged_user_ids,
      }: {
        leadId: string;
        noteId: string;
        summary: string;
        tagged_user_ids: string[];
      }) => ({
        url: `dashboard/leads/${leadId}/notes/${noteId}`,
        method: "POST",
        body: { summary, tagged_user_ids },
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "Notes" }],
    }),

    deleteLeadNote: builder.mutation({
      query: ({ leadId, noteId }: { leadId: string; noteId: string }) => ({
        url: `dashboard/leads/${leadId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "Notes" }],
    }),

    updateLeadStage: builder.mutation({
      query: ({
        leadId,
        current_stage,
        lost_primary_reason,
        lost_reason,
        reason_text,
        cancelled_primary_reason,
        cancelled_reason,
        cancellation_reason_text,
      }: {
        leadId: string;
        current_stage: string;
        lost_primary_reason: string | null;
        lost_reason?: string | null;
        reason_text: string | null;
        cancelled_primary_reason?: string | null;
        cancelled_reason?: string | null;
        cancellation_reason_text?: string | null;
      }) => ({
        url: `dashboard/leads/update/${leadId}`,
        method: "PATCH",
        body: {
          current_stage,
          lost_primary_reason,
          lost_reason,
          reason_text,
          cancelled_primary_reason,
          cancelled_reason,
          cancellation_reason_text,
        },
      }),
      invalidatesTags: [
        { type: "LeadDetails", id: "LeadDetails" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    getLeadMessageAndSessionCount: builder.query({
      query: (leadId: string) => ({
        url: `/dashboard/communication/${leadId}/get-messages-count`,
        method: "GET",
      }),
    }),

    updateLeadDetailsCrm: builder.mutation({
      query: ({
        lead_id,
        data,
      }: {
        lead_id: string;
        data: Record<string, unknown>;
      }) => ({
        url: `/dashboard/leads/update/${lead_id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        { type: "LeadDetails", id: "LeadDetails" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    getAllViewings: builder.query({
      query: (leadId: string) => ({
        url: `/viewings/${leadId}`,
        method: "GET",
      }),
      providesTags: [{ type: "Viewings", id: "AllViewings" }],
    }),

    updateViewingQA: builder.mutation({
      query: ({
        viewingId,
        data,
      }: {
        viewingId: string;
        data: Record<string, unknown>;
      }) => ({
        url: `/viewings/${viewingId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags: { type: "Viewings"; id: string }[] = [
          { type: "Viewings", id: "AllViewings" },
          { type: "Viewings", id: "ViewingsList" },
        ];
        if (arg?.data?.status) {
          tags.push({ type: "Viewings", id: "ViewingsBoard" });
        }
        return tags;
      },
    }),

    updateViewingAssignees: builder.mutation({
      query: ({
        viewingId,
        assignees,
      }: {
        viewingId: string;
        assignees: string[];
      }) => ({
        url: `/viewings/${viewingId}`,
        method: "PATCH",
        body: { assignees },
      }),
      invalidatesTags: [
        { type: "Viewings", id: "AllViewings" },
        { type: "Viewings", id: "ViewingsList" },
        { type: "Viewings", id: "ViewingsBoard" },
      ],
    }),

    deleteViewing: builder.mutation<void, string>({
      query: (viewingId) => ({
        url: `/viewings/${viewingId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Viewings", id: "AllViewings" },
        { type: "Viewings", id: "ViewingsList" },
        { type: "Viewings", id: "ViewingsBoard" },
        { type: "LeadDetails", id: "LeadDetails" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    downloadLeadAttachments: builder.mutation({
      query: ({ lead_id }: { lead_id: string }) => ({
        url: `/dashboard/leads/${lead_id}/attachments/download-attachments`,
        method: "POST",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    getTasksByLeadId: builder.query({
      query: (leadId: string) => ({
        url: `/tasks/lead/${leadId}`,
        method: "GET",
      }),
      providesTags: [{ type: "Tasks", id: "LeadTasks" }],
    }),

    getAllTasks: builder.query<GetAllTasksResponse, GetAllTasksParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.append("page", String(params.page ?? 1));
        searchParams.append("limit", String(params.limit ?? 50));
        searchParams.append("sort_order", params.sort_order ?? "asc");
        if (params.search?.trim())
          searchParams.append("search", params.search.trim());
        params.priorities?.forEach((p) => searchParams.append("priorities", p));
        params.lead_stages?.forEach((s) =>
          searchParams.append("lead_stages", s)
        );
        params.status?.forEach((s) => searchParams.append("status", s));
        if (params.due_today) searchParams.append("due_today", "true");
        if (params.overdue) searchParams.append("overdue", "true");
        if (params.upcoming) searchParams.append("upcoming", "true");
        if (params.completed) searchParams.append("completed", "true");
        params.assignee_ids?.forEach((id) =>
          searchParams.append("assignee_ids", id)
        );
        params.property_ids?.forEach((id) =>
          searchParams.append("property_ids", String(id))
        );
        params.city_ids?.forEach((id) =>
          searchParams.append("city_ids", String(id))
        );
        params.country_ids?.forEach((id) =>
          searchParams.append("country_ids", String(id))
        );
        if (params.timezone) searchParams.append("timezone", params.timezone);
        return {
          url: `/tasks/?${searchParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Tasks", id: "AllTasks" }],
    }),

    getTaskAnalytics: builder.query<TaskAnalyticsResponse, void>({
      query: () => ({
        url: "/tasks/analytics",
        method: "GET",
      }),
      providesTags: [{ type: "Tasks", id: "TaskAnalytics" }],
    }),

    deleteLeadTask: builder.mutation({
      query: ({ taskId, leadId }: { taskId: string; leadId: string }) => ({
        url: `/tasks/${taskId}?lead_id=${leadId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LeadTasks" },
        { type: "Tasks", id: "AllTasks" },
        { type: "Tasks", id: "TaskAnalytics" },
        { type: "LeadDetails", id: "LeadDetails" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    editLeadTask: builder.mutation({
      query: ({
        taskId,
        data,
      }: {
        taskId: string;
        data: Partial<TaskItem & { lead_id?: string }>;
      }) => ({
        url: `/tasks/${taskId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LeadTasks" },
        { type: "Tasks", id: "AllTasks" },
        { type: "Tasks", id: "TaskAnalytics" },
        { type: "LeadDetails", id: "LeadLogs" },
        { type: "LeadDetails", id: "LeadDetails" },
      ],
    }),

    getLeadLogs: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `dashboard/leads/${id}/logs`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadDetails", id: "LeadLogs" }],
    }),

    assignAgentToLead: builder.mutation({
      query: ({
        leadId,
        agentIds,
        entityType,
      }: {
        leadId: string;
        agentIds: string[];
        entityType?: string;
      }) => ({
        url: `/lms/leads/assign-agent`,
        method: "POST",
        body: {
          lead_id: leadId,
          agent_id: agentIds,
          ...(entityType && { entity_type: entityType }),
        },
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "LeadDetails" }],
    }),
    getEmailTemplates: builder.query<
      {
        items: Array<{
          _id: string;
          name: string;
          subject: string;
          content: string;
          created_by: string;
          last_updated_by: string;
          created_at: string;
          updated_at: string;
          sort_order: string;
          attachments?: {
            s3_key: string;
            file_name: string;
            content_type: string;
            size_in_bytes: number;
          }[];
        }>;
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
        has_next: boolean;
        has_previous: boolean;
      },
      {
        page?: number;
        page_size?: number;
        template_type?: string;
        user_id?: string;
        sort_by?: "name" | "created_at";
        sort_order?: "asc" | "desc";
        query?: string;
      }
    >({
      query: ({
        page = 1,
        page_size = 10,
        template_type,
        user_id,
        sort_by = "created_at",
        sort_order = "desc",
        query,
      }) => ({
        url: `/email-template/?page=${page}&page_size=${page_size}${
          template_type ? `&template_type=${template_type}` : ""
        }${
          user_id ? `&user_id=${user_id}` : ""
        }&sort_by=${sort_by}&sort_order=${sort_order}${
          query ? `&query=${encodeURIComponent(query)}` : ""
        }`,
        method: "GET",
      }),
      providesTags: [{ type: "EmailTemplates", id: "List" }],
    }),
    deleteEmailTemplate: builder.mutation<void, string>({
      query: (template_id: string) => ({
        url: `/email-template/${template_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "EmailTemplates", id: "List" },
        { type: "EmailTemplates", id: "Categories" },
      ],
    }),
    markEmailTemplateAsFavorite: builder.mutation({
      query: ({
        template_id,
        isFavorite,
      }: {
        template_id: string;
        isFavorite: boolean;
      }) => ({
        url: `/email-template/${template_id}`,
        method: "PATCH",
        body: { favorite: isFavorite },
      }),
      invalidatesTags: [
        { type: "EmailTemplates", id: "List" },
        { type: "EmailTemplates", id: "Categories" },
      ],
    }),
    searchEmailTemplates: builder.query<
      {
        items: Array<{
          _id: string;
          name: string;
          subject: string;
          content: string;
          created_by: string;
          last_updated_by: string;
          created_at: string;
          updated_at: string;
        }>;
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
        has_next: boolean;
        has_previous: boolean;
      },
      { search_request: { query: string }; template_type?: string }
    >({
      query: ({ search_request, template_type }) => ({
        url: `/email-template/search${template_type ? `?template_type=${template_type}` : ""}`,
        method: "POST",
        body: search_request,
      }),
      providesTags: [{ type: "EmailTemplates", id: "Search" }],
    }),
    getEmailTemplateCategories: builder.query<
      Array<{ id: string; name: string; count: number }>,
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `/email-template/categories?user_id=${user_id}`,
        method: "GET",
      }),
      providesTags: [{ type: "EmailTemplates", id: "Categories" }],
    }),
    getLeadPropertiesList: builder.query({
      query: ({
        managerId,
        leadId,
      }: {
        managerId: string;
        leadId?: string;
      }) => {
        const params = new URLSearchParams();
        if (leadId) params.set("lead_id", leadId);
        params.set("page", "1");
        params.set("page_size", "50");
        return {
          url: `/pms/properties/${managerId}?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getCalendarSyncStatus: builder.query<
      { is_calendar_sync_available: boolean },
      void
    >({
      query: () => ({
        url: "/viewings/calendar-sync-status",
        method: "GET",
      }),
    }),
    createViewing: builder.mutation({
      query: (payload: {
        client_id: string;
        name: string;
        email: string;
        viewing_type: "physical" | "virtual";
        event_name: string;
        scheduled_at: string;
        lead_id: string;
        property_details?: {
          chosen_properties: Array<{
            property_id: number;
            property_name: string;
          }>;
          room_type?: string;
        };
        assignees?: string[];
        virtual_meet_details?: {
          meeting_platform?: string;
          joining_link?: string;
        };
        physical_meet_details?: {
          location?: string;
        };
        notes?: string;
        sync_to_calendar?: boolean;
      }) => ({
        url: `/viewings/${payload.client_id}/create_viewing`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "Viewings", id: "UpcomingViewing" },
        { type: "Viewings", id: "UpcomingViewingCount" },
        { type: "Viewings", id: "AllViewings" },
        { type: "Viewings", id: "ViewingsBoard" },
        { type: "Viewings", id: "ViewingsList" },
      ],
    }),
    addViewingQA: builder.mutation({
      query: ({ viewingId, data }) => ({
        url: `/viewings/${viewingId}/viewing_qna`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Viewings", id: "UpcomingViewing" },
        { type: "Viewings", id: "UpcomingViewingCount" },
        { type: "Viewings", id: "AllViewings" },
      ],
    }),
    addLeadTask: builder.mutation({
      query: (payload: {
        name?: string;
        description: string;
        due_date: string;
        priority: "high" | "normal" | "low";
        entity_id: string;
        entity_type: "lead";
        assignees: string[];
        client_name?: string;
        copy_from_lead?: boolean;
        destination_city?: string;
        destination_city_id?: number;
        destination_country?: string;
        destination_country_id?: number;
        choosen_props?: { property_id: number; property_name: string; room_type?: string | null }[];
      }) => ({
        url: `/tasks/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LeadTasks" },
        { type: "Tasks", id: "AllTasks" },
        { type: "Tasks", id: "TaskAnalytics" },
        { type: "LeadDetails", id: "LeadLogs" },
        { type: "LeadDetails", id: "LeadDetails" },
      ],
    }),
    bulkUpdateTasks: builder.mutation<
      BulkUpdateTasksResponse,
      { task_ids: string[]; update_data: Record<string, string> }
    >({
      query: (payload) => ({
        url: `/tasks/bulk-update`,
        method: "PATCH",
        body: payload,
        validateStatus: (response: { status: number }) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LeadTasks" },
        { type: "Tasks", id: "AllTasks" },
        { type: "Tasks", id: "TaskAnalytics" },
      ],
    }),
    bulkDeleteTasks: builder.mutation<
      BulkUpdateTasksResponse,
      { task_ids: string[] }
    >({
      query: (payload) => ({
        url: `/tasks/bulk-delete`,
        method: "DELETE",
        body: payload,
        validateStatus: (response: { status: number }) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LeadTasks" },
        { type: "Tasks", id: "AllTasks" },
        { type: "Tasks", id: "TaskAnalytics" },
        { type: "LeadDetails", id: "LeadDetails" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),
    createEmailTemplate: builder.mutation({
      query: (payload: {
        name: string;
        subject?: string;
        content: string;
        attachments?: {
          s3_key: string;
          file_name: string;
          content_type: string;
          size_in_bytes: number;
        }[];
      }) => ({
        url: `/email-template/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "EmailTemplates", id: "List" },
        { type: "EmailTemplates", id: "Categories" },
      ],
    }),
    uploadEmailAttachment: builder.mutation({
      query: (payload: {
        attachment: File;
        source: string;
        bucket_type?: string;
      }) => {
        const formData = new FormData();
        formData.append("files", payload.attachment);
        formData.append("source", payload.source);
        if (payload.bucket_type) {
          formData.append("bucket_type", payload.bucket_type);
        }

        return {
          url: `/attachments/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    getAttachment: builder.mutation<string, { s3_key: string }>({
      query: ({ s3_key }) => ({
        url: `/attachments/?s3_key=${s3_key}`,
        method: "GET",
      }),
    }),
    editEmailTemplate: builder.mutation({
      query: (payload: {
        template_id: string;
        name?: string;
        subject?: string;
        content?: string;
        attachments?: {
          s3_key: string;
          file_name: string;
          content_type: string;
          size_in_bytes: number;
        }[];
        favorite?: boolean;
      }) => ({
        url: `/email-template/${payload.template_id}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: [
        { type: "EmailTemplates", id: "List" },
        { type: "EmailTemplates", id: "Search" },
        { type: "EmailTemplates", id: "Categories" },
      ],
    }),
    getLeadCities: builder.query({
      query: (countryIds: string[] | number[]) => ({
        url: `/dashboard/pms/cities?country_id=${countryIds.join(",")}`,
        method: "GET",
      }),
    }),
    getLeadCountries: builder.query({
      query: () => ({
        url: `/dashboard/pms/countries`,
        method: "GET",
      }),
    }),
    getLeadUniversities: builder.query({
      query: (id: string | string[]) => ({
        url: `/dashboard/pms/universities?city_id=${
          Array.isArray(id) ? id.join(",") : id
        }`,
        method: "GET",
      }),
    }),
    createLead: builder.mutation({
      query: (payload) => ({
        url: `/dashboard/leads/create`,
        method: "POST",
        body: payload,
      }),
    }),

    // Client-configured prefill values for the create-lead form (nulls when the
    // client has none configured).
    getLeadCreateDefaults: builder.query<LeadCreateDefaults, void>({
      query: () => ({
        url: `/dashboard/leads/create/defaults`,
        method: "GET",
      }),
    }),

    createVfLead: builder.mutation({
      query: (payload) => ({
        url: `/dashboard/leads/create`,
        method: "POST",
        body: payload,
      }),
    }),

    getLeadCheckListItems: builder.query({
      query: (leadId: string) => ({
        url: `/lms/leads/client/lead/${leadId}/checklist`,
        method: "GET",
      }),
    }),

    updateLeadCheckListItems: builder.mutation({
      query: ({
        leadId,
        checkListItems,
      }: {
        leadId: string;
        checkListItems: {
          checklist_id: string;
          action: "checked" | "unchecked";
        }[];
      }) => ({
        url: `/lms/leads/client/lead/${leadId}/checklist/complete`,
        method: "POST",
        body: { checklist_update_data: [...checkListItems] },
      }),
    }),

    getLeadStageFieldOptions: builder.query({
      query: (params?: { view_id?: string }) => ({
        url: `/settings/lead-form/field-options/current_stage${params?.view_id ? `?view_id=${params.view_id}` : ""}`,
        method: "GET",
      }),
    }),

    getLeadContacts: builder.query({
      query: (leadId: string) => ({
        url: `/lead-contacts/${leadId}`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadContacts", id: "LeadContacts" }],
    }),

    getLeadContactsCount: builder.query({
      query: (leadId: string) => ({
        url: `/lead-contacts/${leadId}/count`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadContacts", id: "LeadContactsCount" }],
    }),

    addLeadContact: builder.mutation({
      query: (payload: {
        lead_id: string;
        name: string;
        email?: string;
        phone?: string;
        phone_country_code?: string;
        designation?: string;
      }) => ({
        url: `/lead-contacts/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "LeadContacts", id: "LeadContacts" },
        { type: "LeadContacts", id: "LeadContactsCount" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    editLeadContact: builder.mutation({
      query: ({
        contactId,
        data,
      }: {
        contactId: string;
        data: {
          name?: string;
          email?: string | null;
          phone?: string | null;
          phone_country_code?: string | null;
          designation?: string | null;
        };
      }) => ({
        url: `/lead-contacts/${contactId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        { type: "LeadContacts", id: "LeadContacts" },
        { type: "LeadContacts", id: "LeadContactsCount" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),

    deleteLeadContact: builder.mutation({
      query: (contactId: string) => ({
        url: `/lead-contacts/${contactId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "LeadContacts", id: "LeadContacts" },
        { type: "LeadContacts", id: "LeadContactsCount" },
        { type: "LeadDetails", id: "LeadLogs" },
      ],
    }),
    getLeadFormSubmissions: builder.query<
      {
        submissions: {
          submission_id: string;
          form_id: string;
          form_name: string;
          source_url: string;
          submitted_fields: {
            label: string;
            value: string;
          }[];
          created_at: string;
        }[];
        total: number;
        page: number;
        page_size: number;
      },
      string
    >({
      query: (leadId) => ({
        url: `/dashboard/leads/${leadId}/form-submissions`,
        method: "GET",
      }),
    }),

    getCrmViews: builder.query({
      query: (params?: { page?: number; limit?: number; search?: string }) => ({
        url: "/dashboard/crm/views",
        method: "GET",
        params,
      }),
      providesTags: [{ type: "CrmViews", id: "List" }],
    }),
    getCrmView: builder.query({
      query: (viewId: string) => ({
        url: `/dashboard/crm/views/${viewId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, viewId) => [
        { type: "CrmViews", id: viewId },
      ],
    }),
    createCrmView: builder.mutation({
      query: (body: {
        name: string;
        scope: "private" | "public";
        filter_groups: unknown[];
        logical_operator: "and" | "or";
        emoji_icon: string;
      }) => ({
        url: "/dashboard/crm/views",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CrmViews", id: "List" }],
    }),
    updateCrmView: builder.mutation({
      query: ({
        viewId,
        ...body
      }: {
        viewId: string;
        filter_groups?: unknown[];
        logical_operator?: "and" | "or";
      }) => ({
        url: `/dashboard/crm/views/${viewId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { viewId }) => [
        { type: "CrmViews", id: "List" },
        { type: "CrmViews", id: viewId },
      ],
    }),
    deleteCrmView: builder.mutation({
      query: (viewId: string) => ({
        url: `/dashboard/crm/views/${viewId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CrmViews", id: "List" }],
    }),
    getInboxThreads: builder.query<
      InboxThreadListResponse,
      InboxThreadListParams
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append("page", String(params.page));
        if (params.limit) searchParams.append("limit", String(params.limit));
        if (params.search) searchParams.append("search", params.search);
        if (params.mailbox_email)
          searchParams.append("mailbox_email", params.mailbox_email);
        if (params.inbox_filter)
          searchParams.append("inbox_filter", params.inbox_filter);
        if (params.sort_order)
          searchParams.append("sort_order", params.sort_order);
        if (params.start_date)
          searchParams.append("start_date", params.start_date);
        if (params.end_date) searchParams.append("end_date", params.end_date);
        params.assigned_agent_ids?.forEach((id) =>
          searchParams.append("assigned_agent_ids", id)
        );
        return { url: `/inbox/threads?${searchParams.toString()}` };
      },
      // queryFn: (params) => ({ data: getMockThreadListResponse(params) }),
    }),

    getInboxSummary: builder.query<
      InboxSummaryResponse,
      InboxSummaryParams | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          if (params.search) searchParams.append("search", params.search);
          if (params.mailbox_email)
            searchParams.append("mailbox_email", params.mailbox_email);
          if (params.start_date)
            searchParams.append("start_date", params.start_date);
          if (params.end_date) searchParams.append("end_date", params.end_date);
          params.assigned_agent_ids?.forEach((id) =>
            searchParams.append("assigned_agent_ids", id)
          );
        }
        const qs = searchParams.toString();
        return { url: `/inbox/threads/summary${qs ? `?${qs}` : ""}` };
      },
      // queryFn: () => ({ data: getMockSummaryResponse() }),
    }),

    getThreadMails: builder.query<
      InboxThreadMailsResponse,
      { thread_id: string }
    >({
      query: ({ thread_id }) => `/inbox/threads/${thread_id}/mails`,
      // queryFn: ({ thread_id }) => ({ data: getMockThreadMailsResponse(thread_id) }),
    }),

    assignThreadAgents: builder.mutation<InboxThread, InboxAssignAgentsRequest>(
      {
        query: ({ thread_id, assigned_agents }) => ({
          url: `/inbox/threads/${thread_id}/assignees`,
          method: "PUT",
          body: { assigned_agents },
        }),
        // queryFn: ({ thread_id, assigned_agents }) => ({
        //   data: getMockAssignResponse(thread_id, assigned_agents),
        // }),
      }
    ),

    markThreadRead: builder.mutation<void, { thread_id: string }>({
      query: ({ thread_id }) => ({
        url: `/inbox/threads/${thread_id}/read`,
        method: "PATCH",
      }),
      // queryFn: () => ({ data: undefined }),
    }),

    sendInboxMail: builder.mutation<
      InboxMail,
      {
        thread_id?: string;
        from_email: string;
        to: string[];
        cc?: string[];
        bcc?: string[];
        subject: string;
        content: string;
        attachments?: {
          s3_key: string;
          s3_url: string;
          file_name: string;
          content_type: string;
          size_in_bytes: number;
        }[];
        unsubscribe_enabled?: boolean;
      }
    >({
      query: (body) => ({ url: "/inbox/mail", method: "POST", body }),
      // queryFn: (args) => ({
      //   data: {
      //     id: crypto.randomUUID(),
      //     message_id: crypto.randomUUID(),
      //     subject: args.subject,
      //     content: args.content,
      //     from_email: args.from_email,
      //     to: args.to,
      //     cc: args.cc || [],
      //     bcc: args.bcc || [],
      //     attachments: [],
      //     created_by: { id: "current_agent", name: "Current Agent" },
      //     direction: "OUTBOUND",
      //     created_at: new Date().toISOString(),
      //     updated_at: new Date().toISOString(),
      //   } as InboxMail,
      // }),
    }),
    getCrmPipelines: builder.query({
      query: () => ({
        url: "/dashboard/crm/pipelines",
        method: "GET",
      }),
    }),
    getCallLogsByLeadId: builder.query<GetCallLogsResponse, string>({
      query: (leadId) => ({
        url: `/dashboard/leads/${leadId}/call-logs`,
        method: "GET",
      }),
      providesTags: [{ type: "LeadDetails", id: "CallLogs" }],
    }),
    deleteCallLog: builder.mutation({
      query: ({
        leadId,
        callLogId,
      }: {
        leadId: string;
        callLogId: string;
      }) => ({
        url: `/dashboard/leads/${leadId}/call-logs/${callLogId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "LeadDetails", id: "CallLogs" }],
    }),
    createCallLog: builder.mutation({
      query: ({
        leadId,
        ...body
      }: {
        leadId: string;
        title: string;
        body?: string;
        contact_name?: string;
        call_type?: string;
        call_outcome?: string;
        call_direction?: string;
        activity_date?: string;
        owner?: { id: string; name: string };
      }) => ({
        url: `/dashboard/leads/${leadId}/call-logs`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "LeadDetails", id: "CallLogs" }],
    }),
    updateCallLog: builder.mutation({
      query: ({
        leadId,
        callLogId,
        ...body
      }: {
        leadId: string;
        callLogId: string;
        title?: string;
        body?: string;
        call_type?: string;
        call_outcome?: string;
      }) => ({
        url: `/dashboard/leads/${leadId}/call-logs/${callLogId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "LeadDetails", id: "CallLogs" }],
    }),
    getCrmDistinctValues: builder.query<
      { values: string[] },
      CrmAttributionField
    >({
      query: (field) => ({
        url: `/dashboard/crm/distinct/${field}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetLeadsMutation,
  useCrmLeadSearchMutation,
  useLinkLeadMutation,
  useGetLeadDetailsByIdQuery,
  useGetAllLeadStagesQuery,
  useGetConversationChannelCountByIdQuery,
  useGetTranscriptSummaryByIdQuery,
  useGetBillingInformationByIdQuery,
  useGetOutboundCommunicationStatusByIdQuery,
  useUpdateOutboundCommunicationStatusByIdMutation,
  useUpdateGlobalOutboundCommunicationStatusByIdMutation,
  useGetLeadLostStageReasonsQuery,
  useGetLeadCancellationReasonsQuery,
  useGetLeadAttachmentsByIdQuery,
  useGetAllUsersByStatusQuery,
  useGetNotesByLeadIdQuery,
  useAddLeadNoteMutation,
  useEditLeadNoteMutation,
  useDeleteLeadNoteMutation,
  useUpdateLeadStageMutation,
  useGetLeadMessageAndSessionCountQuery,
  useUpdateLeadDetailsCrmMutation,
  useGetAllViewingsQuery,
  useDownloadLeadAttachmentsMutation,
  useGetTasksByLeadIdQuery,
  useGetAllTasksQuery,
  useGetTaskAnalyticsQuery,
  useDeleteLeadTaskMutation,
  useEditLeadTaskMutation,
  useGetLeadLogsQuery,
  useAssignAgentToLeadMutation,
  useGetEmailTemplatesQuery,
  useDeleteEmailTemplateMutation,
  useUpdateViewingQAMutation,
  useUpdateViewingAssigneesMutation,
  useDeleteViewingMutation,
  useGetLeadPropertiesListQuery,
  useCreateViewingMutation,
  useAddViewingQAMutation,
  useMarkEmailTemplateAsFavoriteMutation,
  useAddLeadTaskMutation,
  useBulkUpdateTasksMutation,
  useBulkDeleteTasksMutation,
  useSearchEmailTemplatesQuery,
  useGetEmailTemplateCategoriesQuery,
  useCreateEmailTemplateMutation,
  useUploadEmailAttachmentMutation,
  useGetAttachmentMutation,
  useEditEmailTemplateMutation,
  useGetLeadCitiesQuery,
  useGetLeadCountriesQuery,
  useGetLeadUniversitiesQuery,
  useCreateLeadMutation,
  useGetLeadCreateDefaultsQuery,
  useCreateVfLeadMutation,
  useGetLeadCheckListItemsQuery,
  useUpdateLeadCheckListItemsMutation,
  useGetLeadStageFieldOptionsQuery,
  useGetLeadContactsQuery,
  useGetLeadContactsCountQuery,
  useAddLeadContactMutation,
  useEditLeadContactMutation,
  useDeleteLeadContactMutation,
  useGetLeadFormSubmissionsQuery,
  useGetCrmViewsQuery,
  useGetCrmViewQuery,
  useCreateCrmViewMutation,
  useUpdateCrmViewMutation,
  useDeleteCrmViewMutation,
  useGetCrmPipelinesQuery,
  useLazyGetInboxThreadsQuery,
  useGetInboxSummaryQuery,
  useLazyGetThreadMailsQuery,
  useAssignThreadAgentsMutation,
  useMarkThreadReadMutation,
  useSendInboxMailMutation,
  useGetCallLogsByLeadIdQuery,
  useDeleteCallLogMutation,
  useCreateCallLogMutation,
  useUpdateCallLogMutation,
  useGetCalendarSyncStatusQuery,
  useGetCrmDistinctValuesQuery,
} = crmApi;
